import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { CheckCircle, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const sessionId = searchParams.get('session_id');
  
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [businessDetails, setBusinessDetails] = useState({
    business_name: '',
    contact_name: '',
    email: '',
    phone: '',
    service_type: '',
    additional_info: ''
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      navigate('/');
    }
  }, [sessionId]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      setPaymentStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/payment/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setPaymentStatus('paid');
        return;
      } else if (response.data.status === 'expired') {
        setPaymentStatus('expired');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setPaymentStatus('error');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBusinessDetails(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleServiceTypeChange = (value) => {
    setBusinessDetails(prev => ({
      ...prev,
      service_type: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(`${API}/business-details`, {
        session_id: sessionId,
        ...businessDetails
      });
      
      setFormSubmitted(true);
      toast({
        title: "Details Submitted!",
        description: "We'll start building your website and contact you soon.",
      });
    } catch (error) {
      console.error('Error submitting business details:', error);
      toast({
        title: "Error",
        description: "Something went wrong. Please contact us directly.",
        variant: "destructive",
      });
    }
  };

  if (paymentStatus === 'checking') {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="text-center">
          <Loader2 className="h-16 w-16 animate-spin mx-auto text-blue-600 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Confirming Your Payment...</h2>
          <p className="text-gray-600">Please wait while we verify your transaction.</p>
        </div>
      </div>
    );
  }

  if (paymentStatus === 'expired' || paymentStatus === 'error' || paymentStatus === 'timeout') {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="text-red-600">Payment Issue</CardTitle>
            <CardDescription>
              {paymentStatus === 'expired' && 'Your payment session has expired.'}
              {paymentStatus === 'timeout' && 'Payment verification timed out.'}
              {paymentStatus === 'error' && 'There was an error processing your payment.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">Please try again or contact us for assistance.</p>
            <Button onClick={() => navigate('/')} className="w-full bg-blue-600 hover:bg-blue-700">
              Return to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (formSubmitted) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <div className="mx-auto mb-4">
              <CheckCircle className="h-16 w-16 text-green-600" />
            </div>
            <CardTitle className="text-center text-2xl">All Set!</CardTitle>
            <CardDescription className="text-center text-base">
              Thank you for signing up. We've received your details and will start building your website right away.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 text-center mb-6">
              We'll contact you within 24 hours to get started.
            </p>
            <Button onClick={() => navigate('/')} className="w-full bg-blue-600 hover:bg-blue-700">
              Return to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Payment Successful!</h1>
          <p className="text-xl text-gray-600">
            Thank you for choosing Local Biz Websites.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Tell Us About Your Business</CardTitle>
            <CardDescription className="text-base">
              Please provide your business details so we can create the perfect website for you.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="business_name" className="text-base">Business Name *</Label>
                <Input
                  id="business_name"
                  name="business_name"
                  type="text"
                  required
                  value={businessDetails.business_name}
                  onChange={handleInputChange}
                  className="mt-1 text-base h-11"
                  placeholder="Smith Plumbing Services"
                />
              </div>
              
              <div>
                <Label htmlFor="contact_name" className="text-base">Your Name *</Label>
                <Input
                  id="contact_name"
                  name="contact_name"
                  type="text"
                  required
                  value={businessDetails.contact_name}
                  onChange={handleInputChange}
                  className="mt-1 text-base h-11"
                  placeholder="John Smith"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-base">Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={businessDetails.email}
                  onChange={handleInputChange}
                  className="mt-1 text-base h-11"
                  placeholder="john@smithplumbing.com"
                />
              </div>

              <div>
                <Label htmlFor="phone" className="text-base">Phone Number *</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  required
                  value={businessDetails.phone}
                  onChange={handleInputChange}
                  className="mt-1 text-base h-11"
                  placeholder="07123 456789"
                />
              </div>

              <div>
                <Label htmlFor="service_type" className="text-base">Type of Service *</Label>
                <Select onValueChange={handleServiceTypeChange} required>
                  <SelectTrigger className="mt-1 h-11">
                    <SelectValue placeholder="Select your service type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="plumbing">Plumbing</SelectItem>
                    <SelectItem value="electrician">Electrician</SelectItem>
                    <SelectItem value="heating">Heating & Boiler</SelectItem>
                    <SelectItem value="building">Building & Construction</SelectItem>
                    <SelectItem value="gardening">Gardening & Landscaping</SelectItem>
                    <SelectItem value="cleaning">Cleaning Services</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="additional_info" className="text-base">Additional Information</Label>
                <Textarea
                  id="additional_info"
                  name="additional_info"
                  value={businessDetails.additional_info}
                  onChange={handleInputChange}
                  rows={4}
                  className="mt-1 text-base"
                  placeholder="Tell us anything else that would help us build your perfect website..."
                />
              </div>

              <Button 
                type="submit" 
                className="w-full bg-blue-600 hover:bg-blue-700 text-white text-base h-12"
              >
                Submit Details & Start Building
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PaymentSuccess;